import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { invites } from '@/api/invites';
import { fetchMe } from '@/api/auth';
import { useAuth } from '@/lib/auth-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { ApiError } from '@/api/client';

const schema = z
  .object({
    password: z.string().min(12, 'Password must be at least 12 characters'),
    confirm: z.string().min(1, 'Please confirm your password'),
  })
  .refine((v) => v.password === v.confirm, {
    message: 'Passwords do not match',
    path: ['confirm'],
  });
type Values = z.infer<typeof schema>;

interface InviteInfo {
  email: string;
  role: string;
  resellerName: string | null;
  displayName: string | null;
  expiresAt: string;
}

export function AcceptInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [invite, setInvite] = useState<InviteInfo | null>(null);
  // Deviation B: split lookupError (fatal full-page) from submitError (inline)
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // When the account already exists in this scope, show a dedicated callout
  // instead of an inline error, so the user knows to log in / use forgot password.
  const [accountExists, setAccountExists] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!token) { setLookupError('This invite link is invalid or has expired.'); return; }
    let ignore = false;
    invites
      .lookup(token)
      .then((data) => { if (!ignore) setInvite(data); })
      .catch(() => { if (!ignore) setLookupError('This invite link is invalid or has expired.'); });
    return () => { ignore = true; };
  }, [token]);

  // Deviation B: full-page error for lookup failures
  if (lookupError) {
    return (
      <section className="mx-auto w-full max-w-md px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <p className="text-sm text-destructive">{lookupError}</p>
      </section>
    );
  }

  if (!invite) {
    return (
      <section className="mx-auto w-full max-w-md px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Loading invite…</p>
          </CardContent>
        </Card>
      </section>
    );
  }

  // Account already exists in this scope — tell the user to log in instead.
  if (accountExists) {
    return (
      <section className="mx-auto w-full max-w-md px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <Card>
          <CardHeader>
            <CardTitle>Account already exists</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <p className="text-muted-foreground">
              An account for <strong>{invite.email}</strong> already exists in this
              organization. You don't need to accept this invite — just log in with
              your existing password.
            </p>
            <p className="text-muted-foreground">
              If you've forgotten your password, use the{' '}
              <Link to="/forgot-password" className="underline text-foreground hover:text-primary">
                Forgot password
              </Link>{' '}
              link. If you need a new temporary password, ask your administrator to
              reset it from your user settings.
            </p>
            <Button asChild className="w-full mt-2">
              <Link to="/login">Go to login</Link>
            </Button>
          </CardContent>
        </Card>
      </section>
    );
  }

  async function onSubmit(values: Values) {
    setSubmitError(null);
    try {
      const r = await invites.accept(token, values.password);
      useAuth.setState({ accessToken: r.access_token });
      const me = await fetchMe();
      useAuth.getState().setSession({
        accessToken: r.access_token,
        user: me.user,
        tenant: me.tenant,
      });
      navigate('/dashboard');
    } catch (err) {
      // Account already exists in this scope — show dedicated callout.
      if ((err as ApiError).code === 'email_already_exists_in_scope') {
        setAccountExists(true);
        return;
      }
      // Deviation B: inline submit error, does not replace the form
      setSubmitError(err instanceof Error ? err.message : 'Failed to accept invite.');
    }
  }

  const inviteBlurb = invite.resellerName
    ? `You've been invited to ${invite.resellerName} on Ivrello as `
    : `You've been invited on Ivrello as `;

  return (
    <section className="container py-16 max-w-md">
      <Card>
        <CardHeader>
          <CardTitle>Accept your invitation</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            {inviteBlurb}
            <strong>{invite.email}</strong>. Set a password to continue.
          </p>
          <form className="space-y-4" noValidate onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-1">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                aria-invalid={errors.password != null}
                aria-describedby={errors.password ? 'password-error' : undefined}
                {...register('password')}
              />
              {errors.password && (
                <p id="password-error" role="alert" className="text-xs text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="confirm">Confirm password</Label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                aria-invalid={errors.confirm != null}
                aria-describedby={errors.confirm ? 'confirm-error' : undefined}
                {...register('confirm')}
              />
              {errors.confirm && (
                <p id="confirm-error" role="alert" className="text-xs text-destructive">
                  {errors.confirm.message}
                </p>
              )}
            </div>

            {/* Deviation B: inline submit error before button */}
            {submitError && (
              <p role="alert" className="text-sm text-destructive">
                {submitError}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Accepting…' : 'Accept and continue'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
